import type { Locale } from "@kmstories/shared";
import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import { Layout } from "./components/Layout.js";
import { ToastProvider } from "./components/Toast.js";
import { LocaleProvider, localizedPath, readStoredLocale } from "./i18n/LocaleProvider.js";
import { AdminApp } from "./pages/admin/AdminApp.js";
import { ClipPage } from "./pages/Clip.js";
import { ClipsPage } from "./pages/Clips.js";
import { ContactPage } from "./pages/Contact.js";
import { FilmPage } from "./pages/Film.js";
import { HomePage } from "./pages/Home.js";
import { LegalPage } from "./pages/Legal.js";
import { NewsletterConfirmPage, NewsletterUnsubscribePage } from "./pages/Newsletter.js";
import { NotFoundPage } from "./pages/NotFound.js";
import { SharePage } from "./pages/Share.js";
import { SupportPage } from "./pages/Support.js";
import { WithdrawPage } from "./pages/Withdraw.js";

/**
 * English at the root, Persian under /fa. The same page tree is mounted twice
 * so a shared link keeps its language and every page has a stable URL in both.
 */
export function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <Routes>
          <Route path="/fa/*" element={<LocaleRoot locale="fa" />} />
          <Route path="/*" element={<LocaleRoot locale="en" />} />
        </Routes>
      </ToastProvider>
    </BrowserRouter>
  );
}

function LocaleRoot({ locale }: { locale: Locale }) {
  const location = useLocation();
  const isAdmin = /^\/(fa\/)?admin(\/|$)/.test(location.pathname);
  return (
    <LocaleProvider locale={locale}>
      {locale === "en" && <FirstVisitRedirect />}
      {isAdmin ? (
        <Routes>
          <Route path="admin/*" element={<AdminApp />} />
        </Routes>
      ) : (
        <Layout>
          <ScrollToTop />
          <Routes>
            <Route index element={<HomePage />} />
            <Route path="films/my-child" element={<FilmPage />} />
            <Route path="share" element={<SharePage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="clips" element={<ClipsPage />} />
            <Route path="c/:slug" element={<ClipPage />} />
            <Route path="privacy" element={<LegalPage doc="privacy" />} />
            <Route path="terms" element={<LegalPage doc="terms" />} />
            <Route path="w/:token" element={<WithdrawPage />} />
            <Route path="newsletter/confirm" element={<NewsletterConfirmPage />} />
            <Route path="newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
            <Route path="fa" element={<Navigate to="/fa" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Layout>
      )}
    </LocaleProvider>
  );
}

/** A Persian-language browser landing on the English root, with no remembered choice, goes to /fa. */
function FirstVisitRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (readStoredLocale()) return;
    const lang = (navigator.language || "").toLowerCase();
    if (lang.startsWith("fa") && !location.pathname.startsWith("/admin")) {
      navigate(localizedPath(location.pathname, "fa") + location.search + location.hash, { replace: true });
    }
    // Only on first mount: the provider stores a choice right after this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0 });
  }, [pathname, hash]);
  return null;
}
